import React, { useState } from "react";
import { supabase } from "./supabase";
import "./App.css";

const STEPS = ["Your Store", "Contact Info", "Ready"];

export default function Onboarding({ user, onComplete }) {
  const [step, setStep] = useState(0);
  const [form, setForm] = useState({
    store_name: "",
    store_tin: "",
    manager_name: user?.user_metadata?.full_name ?? "",
    store_email: user?.email ?? "",
    store_phone: "",
    store_address: "",
  });
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [launched, setLaunched] = useState(false);

  function set(key, value) {
    setForm((prev) => ({ ...prev, [key]: value }));
    setError(null);
  }

  function validateStep() {
    if (step === 0) {
      if (!form.store_name.trim()) {
        setError("Store name is required.");
        return false;
      }
      if (!form.manager_name.trim()) {
        setError("Your name is required.");
        return false;
      }
    }
    if (step === 1) {
      if (!form.store_email.trim()) {
        setError("Store email is required.");
        return false;
      }
      if (!form.store_phone.trim()) {
        setError("Store phone is required.");
        return false;
      }
      if (!form.store_address.trim()) {
        setError("Store address is required.");
        return false;
      }
    }
    return true;
  }

  function nextStep() {
    if (!validateStep()) return;
    setStep((s) => s + 1);
  }

  async function handleComplete() {
    setError(null);
    setLoading(true);

    const { data, error: rpcError } = await supabase.rpc(
      "create_store_with_owner",
      {
        p_store_name: form.store_name.trim(),
        p_store_email: form.store_email.trim().toLowerCase(),
        p_store_phone: form.store_phone.trim(),
        p_store_address: form.store_address.trim(),
        p_store_tin: form.store_tin.trim(),
        p_manager_name: form.manager_name.trim(),
      },
    );

    if (rpcError) {
      setError("Store setup failed: " + rpcError.message);
      setLoading(false);
      return;
    }

    // Show celebration then transition
    setLoading(false);
    setLaunched(true);
    setTimeout(() => {
      onComplete(data);
    }, 2800);
  }

  const inputStyle = {
    width: "100%",
    height: "42px",
    padding: "0 14px",
    background: "var(--elevated)",
    border: "1px solid var(--border)",
    borderRadius: "var(--radius)",
    color: "var(--text)",
    fontSize: "14px",
    fontFamily: "var(--font)",
    outline: "none",
    transition: "border 0.15s",
    boxSizing: "border-box",
  };

  if (launched) {
    return (
      <div className="launch-overlay">
        <div className="launch-burst" />
        <div className="launch-burst launch-burst-2" />
        <div className="launch-content">
          <div className="launch-logo-wrap">
            <svg width="72" height="72" viewBox="0 0 680 320" fill="none">
              <path
                d="M240 210 L240 140 Q240 80 300 80 Q360 80 360 140 L360 210"
                stroke="#32D4E0"
                strokeWidth="28"
                strokeLinecap="round"
                fill="none"
              />
              <path
                d="M258 210 L258 148 Q258 108 300 108 Q342 108 342 148 L342 210"
                stroke="#FFB340"
                strokeWidth="28"
                strokeLinecap="round"
                fill="none"
              />
              <line
                x1="228"
                y1="210"
                x2="372"
                y2="210"
                stroke="#32D4E0"
                strokeWidth="20"
                strokeLinecap="round"
              />
              <circle cx="300" cy="80" r="28" fill="#32D4E0" />
              <circle cx="300" cy="108" r="20" fill="#FFB340" />
            </svg>
          </div>
          <div className="launch-check">✓</div>
          <h2 className="launch-title">Your store is live!</h2>
          <p className="launch-sub">
            {form.store_name} is ready. Taking you to your POS...
          </p>
          <div className="launch-progress">
            <div className="launch-progress-bar" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-page">
      <div className="auth-card" style={{ maxWidth: "460px" }}>
        {/* Brand */}
        <div className="auth-brand">
          <svg width="48" height="48" viewBox="0 0 680 320" fill="none">
            <path
              d="M240 210 L240 140 Q240 80 300 80 Q360 80 360 140 L360 210"
              stroke="#32D4E0"
              strokeWidth="28"
              strokeLinecap="round"
              fill="none"
            />
            <path
              d="M258 210 L258 148 Q258 108 300 108 Q342 108 342 148 L342 210"
              stroke="#FFB340"
              strokeWidth="28"
              strokeLinecap="round"
              fill="none"
            />
            <line
              x1="228"
              y1="210"
              x2="372"
              y2="210"
              stroke="#32D4E0"
              strokeWidth="20"
              strokeLinecap="round"
            />
            <circle cx="300" cy="80" r="28" fill="#32D4E0" />
          </svg>
          <span className="auth-brand-name">
            <span style={{ color: "var(--cyan)", fontWeight: 700 }}>Suq</span>
            <span style={{ color: "var(--amber)", fontWeight: 300 }}>POS</span>
          </span>
        </div>

        {/* Step indicator */}
        <div className="onboarding-steps">
          {STEPS.map((s, i) => (
            <div
              key={i}
              className={`onboarding-step-item ${i === step ? "active" : i < step ? "done" : ""}`}
            >
              <div className="onboarding-step-dot">
                {i < step ? "✓" : i + 1}
              </div>
              <span className="onboarding-step-label">{s}</span>
              {i < STEPS.length - 1 && <div className="onboarding-step-line" />}
            </div>
          ))}
        </div>

        {error && <div className="auth-error">⚠ {error}</div>}

        {/* ── Step 0: Store basics ── */}
        {step === 0 && (
          <div className="auth-form">
            <h2 className="auth-title">Tell us about your store</h2>
            <p className="auth-sub">
              This information appears on your receipts and reports.
            </p>

            <div className="auth-field">
              <label>Store Name *</label>
              <input
                style={inputStyle}
                placeholder="e.g. Guadaye Mini Market"
                value={form.store_name}
                onChange={(e) => set("store_name", e.target.value)}
              />
            </div>
            <div className="auth-field">
              <label>Store TIN (optional)</label>
              <input
                style={inputStyle}
                placeholder="e.g. 0012345678"
                value={form.store_tin}
                onChange={(e) => set("store_tin", e.target.value)}
              />
            </div>
            <div className="auth-field">
              <label>Your Name (Manager) *</label>
              <input
                style={inputStyle}
                placeholder="e.g. Alem Tedros"
                value={form.manager_name}
                onChange={(e) => set("manager_name", e.target.value)}
              />
            </div>

            <button className="auth-submit" onClick={nextStep}>
              Continue →
            </button>
          </div>
        )}

        {/* ── Step 1: Contact info ── */}
        {step === 1 && (
          <div className="auth-form">
            <h2 className="auth-title">Contact information</h2>
            <p className="auth-sub">
              How can customers and staff reach your store?
            </p>

            <div className="auth-field">
              <label>Store Email *</label>
              <input
                style={inputStyle}
                type="email"
                placeholder="store@example.com"
                value={form.store_email}
                onChange={(e) => set("store_email", e.target.value)}
              />
            </div>
            <div className="auth-field">
              <label>Store Phone *</label>
              <input
                style={inputStyle}
                placeholder="+251 9xx xxx xxx"
                value={form.store_phone}
                onChange={(e) => set("store_phone", e.target.value)}
              />
            </div>
            <div className="auth-field">
              <label>Store Address *</label>
              <input
                style={inputStyle}
                placeholder="e.g. Addis Ababa, Bole sub-city"
                value={form.store_address}
                onChange={(e) => set("store_address", e.target.value)}
              />
            </div>

            <div style={{ display: "flex", gap: "10px" }}>
              <button
                onClick={() => setStep(0)}
                style={{
                  flex: 1,
                  height: "44px",
                  background: "transparent",
                  border: "1px solid var(--border)",
                  borderRadius: "var(--radius)",
                  color: "var(--text3)",
                  fontSize: "14px",
                  fontFamily: "var(--font)",
                  cursor: "pointer",
                }}
              >
                ← Back
              </button>
              <button
                className="auth-submit"
                style={{ flex: 2 }}
                onClick={nextStep}
              >
                Continue →
              </button>
            </div>
          </div>
        )}

        {/* ── Step 2: Review & launch ── */}
        {step === 2 && (
          <div className="auth-form">
            <h2 className="auth-title">You're all set!</h2>
            <p className="auth-sub">
              Review your store details before launching.
            </p>

            <div
              style={{
                background: "var(--elevated)",
                border: "1px solid var(--border)",
                borderRadius: "var(--radius-lg)",
                padding: "16px",
                marginBottom: "8px",
              }}
            >
              {[
                { label: "Store", value: form.store_name },
                { label: "Manager", value: form.manager_name },
                { label: "Email", value: form.store_email },
                { label: "Phone", value: form.store_phone },
                { label: "Address", value: form.store_address },
                { label: "TIN", value: form.store_tin || "—" },
              ].map((r) => (
                <div
                  key={r.label}
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    padding: "7px 0",
                    borderBottom: "1px solid var(--border)",
                    fontSize: "13px",
                  }}
                >
                  <span style={{ color: "var(--text3)" }}>{r.label}</span>
                  <span
                    style={{
                      color: "var(--text)",
                      fontWeight: 500,
                      textAlign: "right",
                      maxWidth: "60%",
                    }}
                  >
                    {r.value}
                  </span>
                </div>
              ))}
            </div>

            {/* Trial info */}
            <div
              style={{
                background: "var(--cyan-dim)",
                border: "1px solid rgba(50,212,224,0.25)",
                borderRadius: "var(--radius)",
                padding: "12px 14px",
                marginBottom: "8px",
                fontSize: "13px",
                color: "var(--cyan)",
              }}
            >
              🎉 Your 3-month free trial starts today. No payment needed.
            </div>

            <div style={{ display: "flex", gap: "10px" }}>
              <button
                onClick={() => setStep(1)}
                style={{
                  flex: 1,
                  height: "44px",
                  background: "transparent",
                  border: "1px solid var(--border)",
                  borderRadius: "var(--radius)",
                  color: "var(--text3)",
                  fontSize: "14px",
                  fontFamily: "var(--font)",
                  cursor: "pointer",
                }}
              >
                ← Back
              </button>
              <button
                className="auth-submit"
                style={{ flex: 2 }}
                onClick={handleComplete}
                disabled={loading}
              >
                {loading ? "Launching..." : "🚀 Launch my store"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
